import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, FileCheck, PhoneCall, Send, ClipboardList, ArrowRight } from 'lucide-react';
import BackBar from '../components/BackBar';
import Footer from '../components/Footer';
import { fetchJob } from '../api/jobs';
import FlagIcon from '../components/FlagIcon';
import { salaryText, salaryKesText, commissionText, COMMISSION_COVERS_ALL_TEXT } from '../lib/salary';
import './JobDetailPage.css';

const Detail = ({ label, value }) => (value ? (
  <div className="jd-detail-row">
    <span className="jd-detail-label">{label}</span>
    <span className="jd-detail-value">{value}</span>
  </div>
) : null);

const JobDetailPage = () => {
  const { slug } = useParams();
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => { (async () => {
    try { setJob(await fetchJob(slug)); setStatus('ok'); } catch { setStatus('notfound'); }
  })(); }, [slug]);

  // Label each chip with its category — the underlying values are often just "Provided", which is
  // indistinguishable from each other without saying what's actually provided.
  const benefits = [
    job?.accommodation && `Accommodation: ${job.accommodation}`,
    job?.transport && `Transport: ${job.transport}`,
    job?.food && `Food: ${job.food}`,
  ].filter(Boolean);

  return (
    <>
      <BackBar />
      <main className="jd-main container">
        {status === 'loading' && <p className="jd-status">Loading…</p>}
        {status === 'notfound' && (
          <div className="jd-status">
            <h1>Position not found</h1>
            <p>This job may have been filled or removed.</p>
            <Link to="/" className="jd-backlink"><ArrowLeft size={15} /> All jobs</Link>
          </div>
        )}
        {status === 'ok' && job && (
          <div className="jd-grid">
            <article className="jd-content">
              <Link to="/" className="jd-backlink"><ArrowLeft size={15} /> All jobs</Link>
              <h1>{job.title}</h1>
              <p className="jd-sub">
                {[job.country, job.gender, (job.ageFrom || job.ageTo) ? `${job.ageFrom ?? ''}–${job.ageTo ?? ''} yrs` : null, job.experience]
                  .filter(Boolean).join(' · ')}
              </p>

              <div className="jd-summary">
                {job.country && (
                  <div className="jd-sumitem">
                    <span className="jd-lbl"><MapPin size={13} aria-hidden="true" /> Location</span>
                    <span className="jd-val"><FlagIcon country={job.country} /> {job.country}</span>
                  </div>
                )}
                {job.workHours && (
                  <div className="jd-sumitem">
                    <span className="jd-lbl"><Clock size={13} aria-hidden="true" /> Work hours</span>
                    <span className="jd-val">{job.workHours}</span>
                  </div>
                )}
              </div>

              {benefits.length > 0 && (
                <div className="jd-benefits">
                  {benefits.map((b) => {
                    // "Provided" reads as a positive signal — call it out in green. Anything else
                    // (a custom value like "Provided (shared room)" still counts; a negative value
                    // like "Not provided" does not) stays the neutral chip style.
                    const isProvided = /:\s*provided\b/i.test(b);
                    return (
                      <span className={isProvided ? 'jd-bchip jd-bchip-provided' : 'jd-bchip'} key={b}>{b}</span>
                    );
                  })}
                </div>
              )}

              <div className="jd-details">
                <Detail label="Role" value={job.profession} />
                <Detail label="Benefits" value={job.benefits} />
              </div>

              {/* Job description — the demand's own JD if it has one, else the standard JD for the
                  role (server-side fallback in publicJobProjection.ts; both arrive as plain text). */}
              {job.jd && (
                <div className="jd-desc">
                  <b className="jd-desc-title"><ClipboardList size={15} aria-hidden="true" /> Job description</b>
                  <p>{job.jd}</p>
                </div>
              )}

              <div className="jd-timeline">
                <b>What happens after you apply</b>
                <div className="jd-tsteps">
                  <div className="jd-tstep"><span className="jd-tdot"><Send size={13} aria-hidden="true" /></span>Apply online in minutes</div>
                  <div className="jd-tstep"><span className="jd-tdot"><FileCheck size={13} aria-hidden="true" /></span>Our team reviews your documents</div>
                  <div className="jd-tstep"><span className="jd-tdot"><PhoneCall size={13} aria-hidden="true" /></span>We call you to confirm next steps</div>
                </div>
              </div>

              {/* Inline apply CTA — the sticky card handles desktop; this covers narrow screens where
                  the sticky card stacks below the fold-worth of content instead of floating. */}
              <Link to={`/apply/${job.slug}`} className="jd-apply-inline">
                Apply for this job <ArrowRight size={17} aria-hidden="true" />
              </Link>
            </article>

            <aside className="jd-side">
              <div className="jd-stickyapply">
                <div className="jd-sal">{salaryText(job.salary)}</div>
                {salaryKesText(job.salary) && <div className="jd-sal-kes">{salaryKesText(job.salary)}</div>}
                <p className="jd-sal-note">Paid monthly. Full details shared before you accept.</p>
                {/* Commission block: the candidate's own cost, stated in full BEFORE the apply CTA
                    rather than after, with the all-inclusive promise directly under the figure so
                    nobody applies expecting hidden extras. Absent entirely when the demand has no
                    candidate charge or the admin has the field hidden. */}
                {commissionText(job.commission) && (
                  <div className="jd-commission">
                    <div className="jd-commission-label">Commission</div>
                    <div className="jd-commission-amt">{commissionText(job.commission)}</div>
                    {job.commission?.coversAll && (
                      <p className="jd-commission-note">{COMMISSION_COVERS_ALL_TEXT}</p>
                    )}
                  </div>
                )}
                <Link to={`/apply/${job.slug}`} className="jd-apply-btn">
                  Apply for this job <ArrowRight size={17} aria-hidden="true" />
                </Link>
                <p className="jd-sal-time">Takes about 5 minutes</p>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
};
export default JobDetailPage;
